import {
  FcpFullScreenCtaFragment,
  Maybe,
  OneColFullScreenCtaFragment,
  SimpleFullScreenCtaFragment,
  TwoColFullScreenCtaFragment,
} from '@gql'
import React, { PropsWithChildren, ReactNode, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { Grid } from '@material-ui/core'
import { FlexibleContent } from '../flexibleContent'
import { irem, rem } from '../../../utils'
import Image from 'next/image'
import { nonNull } from '../../../utils/nonNull'
import { Button } from '../../button/button'
import { parseHtml } from '../../../utils/htmlParser'

export type FullScreenCtaProps = FcpFullScreenCtaFragment & {
  //
}

export const FullScreenCta: React.FC<FullScreenCtaProps> = ({ fcpFullScreenCta: inner }) => {
  const cta = inner ? inner[0] : null
  const image: ReactNode = useMemo(() => {
    if (!cta || !cta.image) {
      return null
    }

    const image = nonNull(cta.image)[0]

    if (!image) {
      return null
    }

    const { url, width, height, __typename } = image
    const alt = __typename === 'images_Asset' ? image.alt : ''

    if (!url) {
      return null
    }

    return <Image src={url} alt={String(alt ?? '')} width={Number(width)} height={Number(height)} />
  }, [cta])

  if (!cta) {
    return null
  }

  const { fullScreenCallToAction, imagePosition, reverseImageOnMobile } = cta
  const fragment = fullScreenCallToAction?.[0]
  const orientation = imagePosition === 'right' ? 'image-bottom' : 'image-top'

  if (!fragment) {
    return null
  }

  return (
    <StyledWrapper $orientation={orientation} $flipMobile={reverseImageOnMobile}>
      <FlexibleContent row>
        {isSimpleCta(fragment) && (
          <SimpleCtaInner imagePosition={imagePosition} { ...fragment }>
            <StyledImageColumn>
              {image}
            </StyledImageColumn>
          </SimpleCtaInner>
        )}
        {isOneColCta(fragment) && (
          <OneColCtaInner imagePosition={imagePosition} { ...fragment }>
            <StyledImageColumn>
              {image}
            </StyledImageColumn>
          </OneColCtaInner>
        )}
        {isTwoColCta(fragment) && (
          <TwoColCtaInner imagePosition={imagePosition} { ...fragment }>
            {image}
          </TwoColCtaInner>
        )}
      </FlexibleContent>
    </StyledWrapper>
  )
}

const StyledTextColumn = styled(Grid).attrs({ item: true, xs: 12 })<{ $variant?: 'simple' | 'two-col' }>`
    width: 100%;
    position: relative;
    z-index: 1;
    
    ${props => props.theme.breakpoints.up('md')} {
        &&& {
            margin-top: ${props => rem(100, props.theme)};
            margin-bottom: ${props => rem(100, props.theme)};
        }
    }

    ${props => props.$variant === 'simple' && css`
        text-align: center;

        &:first-child {
            margin-block-end: ${props => rem(30, props.theme)};
        }

        &:last-child {
            margin-block-start: ${props => rem(30, props.theme)};
        }

        ${props => props.theme.breakpoints.up('md')} {
            text-align: left;

            &:first-child {
                margin-block-end: 0;
            }

            &:last-child {
                margin-block-start: 0;
            }
        }
    `}

    &:first-child {
        margin-block-start: ${props => rem(65, props.theme)};

        ${props => props.theme.breakpoints.up('md')} {
            margin-block-start: 0;
            margin-right: auto;
        }
    }

    &&:last-child {
        margin-block-start: 0;
        margin-block-end: ${props => rem(55, props.theme)};

        ${props => props.theme.breakpoints.up('md')} {
            margin-left: auto;
            margin-block-end: 0;
        }
    }

    ul {
        list-style: none;

        &:last-child {
            margin-block-end: 0;
        }

        li {
            display: flex;
            align-items: baseline;
            font-size: ${props => rem(18, props.theme)};
            margin-bottom: ${props => rem(5, props.theme)};
            
            a:only-child {
                color: inherit;
                text-decoration: none;
                
                &:hover, &:focus {
                    color: ${props => props.theme.palette.primary.main};
                }
            }

            &::before {
                content: '';
                display: inline-block;
                height: 12px;
                width: 7px;
                background-image: url('/images/chevron_orange.svg');
                background-repeat: no-repeat;
                background-size: contain;
            }
        }
    }
    
    ${props => props.$variant === 'two-col' && css`
        ul:only-child {
            margin: 0;
        }
    `}
`

const StyledImageColumn = styled(Grid).attrs({ item: true, xs: 12, md: 6 })`
  position: relative;
  z-index: 1;
  align-self: flex-end;
    
  // Aka, !kinda-important-but-you-can-overwrite-me-if-you-really-want-to
  &&& {
    margin: 0 auto;
      
    ${props => props.theme.breakpoints.up('md')} {
      margin: 0;
    }

    &:first-child {
      margin-block-start: ${props => rem(25, props.theme)};
    }
  }
    
  img {
    display: block;
    width: 100%;
    height: auto;
    max-width: ${irem(600)};
    margin: 0 auto;
  }
`

type CtaInnerUnion = SimpleFullScreenCtaFragment | OneColFullScreenCtaFragment | TwoColFullScreenCtaFragment

const isSimpleCta = (props: CtaInnerUnion): props is SimpleFullScreenCtaFragment => {
  return props.__typename === 'fullScreenCallToAction_simple_BlockType'
}

const isOneColCta = (props: CtaInnerUnion): props is OneColFullScreenCtaFragment => {
  return props.__typename === 'fullScreenCallToAction_oneColumn_BlockType'
}

const isTwoColCta = (props: CtaInnerUnion): props is TwoColFullScreenCtaFragment => {
  return props.__typename === 'fullScreenCallToAction_twoColumn_BlockType'
}

type GenericCtaProps = PropsWithChildren<{
  imagePosition?: 'left' | 'right' | Maybe<string>
}>

export const SimpleCtaInner: React.FC<GenericCtaProps & SimpleFullScreenCtaFragment> = ({ heading, button, children, imagePosition = 'right' }) => {
  return (
    <Grid container alignItems={'center'} >
      {imagePosition === 'left' && children}
      <StyledTextColumn $variant={'simple'} md={5}>
        {heading && <StyledHeading $variant={'simple'}>{heading}</StyledHeading>}
        {button?.url && <StyledButton $variant={'simple'} href={button.url ?? '#'} target={button.target ?? '_self'}>{button.text}</StyledButton>}
      </StyledTextColumn>
      {imagePosition === 'right' && children}
    </Grid>
  )
}

export const OneColCtaInner: React.FC<GenericCtaProps & OneColFullScreenCtaFragment> = ({ eyebrow, heading, smallSubtext, button, children, imagePosition = 'right' }) => {
  return (
    <Grid container alignItems={'center'}>
      {imagePosition === 'left' && children}
      <StyledTextColumn md={5}>
        <StyledCard>
          <StyledCardInner>
            {eyebrow && <StyledEyebrow>{eyebrow}</StyledEyebrow>}
            {heading && <StyledHeading>{heading}</StyledHeading>}
            {smallSubtext && parseHtml(smallSubtext)}
            {button?.url && <StyledButton href={button.url ?? '#'} target={button.target ?? '_self'}>{button.text}</StyledButton>}
          </StyledCardInner>
        </StyledCard>
      </StyledTextColumn>
      {imagePosition === 'right' && children}
    </Grid>
  )
}

export const TwoColCtaInner: React.FC<GenericCtaProps & TwoColFullScreenCtaFragment> = ({ children, heading, left, right }) => {
  if (!left || !right || !heading) {
    return null
  }

  return (
    <Grid container alignItems={'center'}>
      <StyledTextColumn $variant={'two-col'}>
        <StyledCard $margins>
          <StyledTwoColInner>
            <StyledImageWrapper>
              {children}
            </StyledImageWrapper>
            <Grid item xs={12} md={7}>
              <StyledCardInner $margins>
                <StyledHeading $variant={'two-col'}>
                  {heading}
                </StyledHeading>
                <Grid container>
                  <Grid item xs={12} sm={6}>
                    {parseHtml(left)}
                  </Grid>
                  <Grid item xs={12} sm={6} style={{ marginTop: 0 }}>
                    {parseHtml(right)}
                  </Grid>
                </Grid>
              </StyledCardInner>
            </Grid>
          </StyledTwoColInner>
        </StyledCard>
      </StyledTextColumn>
    </Grid>
  )
}

export const StyledTwoColInner = styled(Grid).attrs({ container: true, alignItems: 'stretch' })`
    ${props => props.theme.breakpoints.up('md')} {
        flex-direction: row-reverse;
    }
`

export const StyledImageWrapper = styled(Grid).attrs({ item: true, xs: 12, md: 5 })`
    --padding: ${props => rem(10, props.theme)};
    
    padding: var(--padding) !important;
    position: relative;
    
    img {
        border-radius: ${props => rem(10, props.theme)};
        height: 100%;
        width: 100%;
        object-fit: cover;
        max-height: ${props => rem(200, props.theme)};
        
        ${props => props.theme.breakpoints.up('sm')} {
            max-height: ${props => rem(400, props.theme)};
        }
        
        ${props => props.theme.breakpoints.up('md')} {
            max-height: none;
            position: absolute;
            top: var(--padding);
            left: var(--padding);
            bottom: var(--padding);
            right: var(--padding);
            width: calc(100% - var(--padding) * 2);
            height: calc(100% - var(--padding) * 2);
        }
    }
    
    ${props => props.theme.breakpoints.down('sm')} {
        && + .MuiGrid-item {
            margin-top: 0;
        }
    }
`

export const StyledEyebrow = styled.em`
  color: #1C1F2A;
  font-family: ${props => props.theme.typography.h5.fontFamily};
  font-size: ${props => rem(14, props.theme)};
  letter-spacing: ${props => rem(2.1, props.theme)};
  font-weight: bold;
  font-style: normal;
  text-transform: uppercase;
`

export const StyledHeading = styled.h2<{ $variant?: 'simple' | 'two-col' }>`
    color: #D5420C;
    font-family: ${props => props.theme.typography.h2.fontFamily};
    font-size: ${props => rem(22, props.theme)};
    font-weight: 600;

    ${props => props.theme.breakpoints.up('md')} {
        font-size: ${props => rem(32, props.theme)};
    }

    ${props => props.$variant === 'simple' && css`
        color: white;
        text-align: center;
        font-size: ${props => rem(33, props.theme)};
        line-height: ${props => rem(38, props.theme)};
        text-shadow: 0px 0px ${props => rem(24, props.theme)} rgba(0, 0, 0, 0.08);
        font-style: italic;
        font-weight: 625;

        ${props => props.theme.breakpoints.up('md')} {
            text-align: left;
            font-size: ${props => rem(55, props.theme)};
            line-height: ${props => rem(60, props.theme)};
        }
    `}
    
    ${props => props.$variant === 'two-col' && css`
        margin-bottom: ${props => rem(20, props.theme)};
        
        ${props => props.theme.breakpoints.up('md')} {
            margin-bottom: ${props => rem(45, props.theme)};
        }
    `}
`

export const StyledButton = styled(Button).attrs({ cta: true })<{ $variant?: 'simple' }>`
    ${props => props.$variant === 'simple' && css`
        margin-block-start: auto;
        margin-block-end: auto;
    `}
`

export const StyledCard = styled.div<{ $margins?: boolean }>`
    border-radius: ${props => rem(15, props.theme)};
    background: white;

    ${props => props.$margins && css`
        margin-block-start: ${props => rem(55, props.theme)};
        margin-block-end: ${props => rem(75, props.theme)};
        
        ${props => props.theme.breakpoints.up('md')} {
            margin-block-start: ${props => rem(110, props.theme)};
            margin-block-end: ${props => rem(110, props.theme)};
        }
    `}
`

export const StyledCardInner = styled.div<{ $margins?: boolean }>`
    padding: ${props => rem(32, props.theme)} ${props => rem(30, props.theme)} ${props => rem(40, props.theme)};

    ${props => props.theme.breakpoints.up('md')} {
        padding: ${props => rem(60, props.theme)} ${props => rem(70, props.theme)} ${props => rem(70, props.theme)};
        
        ${props => props.$margins && css`
            padding: ${props => rem(85, props.theme)} ${props => rem(75, props.theme)} ${props => rem(90, props.theme)};
        `}
    }
`

export const StyledWrapper =  styled.div<{ $orientation?: 'image-top' | 'image-bottom'; $flipMobile?: boolean | null }>`
    background: linear-gradient(0deg, #E37025 30.56%, #EBA719 72.55%);
    position: relative;
    margin: ${props => rem(50, props.theme)} 0;

    ${props => props.theme.breakpoints.up('md')} {
        background: linear-gradient(90deg, #E37025 0%, #EBA719 100%);
        margin: ${props => rem(120, props.theme)} 0;
    }
    
    ${props => props.$flipMobile && css`
        ${props => props.theme.breakpoints.down('sm')} {
            .MuiGrid-container {
                flex-direction: column-reverse;
            }
            
            .MuiGrid-item {
                &:first-child {
                    margin-block-start: 0;
                    margin-block-end: ${props => rem(20, props.theme)};
                }
            }
        }
    `}

    &::before {
        position: absolute;
        content: '';
        top: 0;
        right: 0;
        background: linear-gradient(
                45deg,
                rgba(243, 90, 33, 0.00) 47.9%,
                rgba(238, 65, 35, 0.50) 106.02%
        );
        mix-blend-mode: multiply;
        width: ${props => rem(373, props.theme)};
        height: ${props => rem(349, props.theme)};
    }

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: ${props => rem(415, props.theme)};
        height: ${props => rem(320, props.theme)};
        flex-shrink: 0;
        mix-blend-mode: multiply;
        background: url('/images/cta/bottom.svg');
        
        ${props => props.theme.breakpoints.up('lg')} {
            width: ${props => rem(980, props.theme)};
            height: ${props => rem(636, props.theme)};
            background: url('/images/cta/bottom-desktop.svg');
        }
    }

    ${props => props.$orientation === 'image-bottom' && css`
        background: linear-gradient(270deg, #E37025 0%, #EBA719 100%);
    `}

    // If this is an image-bottom on mobile (flipped image-top, or not-flipped image-bottom) change the gradient
    ${props => ((props.$orientation === 'image-bottom' && !props.$flipMobile) || (props.$orientation === 'image-top' && props.$flipMobile)) && css`
        ${props => props.theme.breakpoints.down('sm')} {
            background: linear-gradient(180deg, #E37025 9.52%, #EBA719 95.9%);

            &::before {
                top: auto;
                bottom: 0;
                right: 0;
                background: linear-gradient(
                        135deg,
                        rgba(243, 90, 33, 0.00) 47.9%,
                        rgba(238, 65, 35, 0.50) 106.02%
                );
            }

            &::after {
                top: 0;
                bottom: auto;
                background: url('/images/cta/top.svg');
                width: ${props => rem(220, props.theme)};
                height: ${props => rem(259, props.theme)};
            }
        }
    `}
}
`